// Wording for Home's league cards and the sidebar's status lines (spec
// 2026-09-23 ledger home). Pure: reads one league object from GET /me/home.
import { clinchMagic } from '../../utils/leagueTimeline.js';
import { formatRecord } from '../../utils/formatting.js';
import { ordinal } from '../recap/frontpage/recapPhrases.js';

const PROVIDERS = { ESPN: 'ESPN', YAHOO: 'Yahoo' };
const f1 = (v) => Number(v).toFixed(1);

export function providerLabel(provider) {
  return PROVIDERS[String(provider || '').toUpperCase()] ?? (provider || '');
}

function lastClause(last) {
  if (!last) return null;
  if (last.opponent == null) return 'Bye last week.';
  const verb = last.result === 'W' ? 'Beat' : last.result === 'L' ? 'Lost to' : 'Tied';
  return `${verb} ${last.opponent} ${f1(last.my_score)}–${f1(last.opp_score)}.`;
}

function magicClause(team, playoff) {
  if (!playoff?.chaser) return null;
  const magic = clinchMagic({ me: team, mePosition: team.rank, chaser: playoff.chaser, spots: playoff.spots, gamesRemaining: playoff.games_remaining });
  return magic ? `Magic number ${magic}.` : null;
}

function nextClause(next) {
  if (!next) return null;
  return next.opponent == null ? 'Bye next week.' : `Next: ${next.opponent}.`;
}

/**
 * True when the league has synced data to open: it has a live season. A
 * league you joined whose sync never completed has none, and opening it lands
 * on "League not set up" — it needs the join-and-sync flow instead.
 */
export function isSynced(league) {
  return Boolean(league) && !league.error && league.season != null;
}

export function homeCardModel(league) {
  const meta = [providerLabel(league.provider), league.week && league.total_weeks ? `week ${league.week} of ${league.total_weeks}` : null]
    .filter(Boolean).join(' · ');
  if (league.error) return { kind: 'error', meta, team: null, rank: null, rankDetail: null, sentence: null, line: null };
  if (!isSynced(league)) {
    return { kind: 'unsynced', meta, team: null, rank: null, rankDetail: null, sentence: null, line: 'Not synced yet' };
  }
  const { team } = league;
  if (!team || league.season_state === 'not_started') {
    return { kind: 'upcoming', meta, team: null, rank: null, rankDetail: null, sentence: null, line: 'Season starts week 1' };
  }
  const mine = league.my_team_id != null && String(league.my_team_id) === String(team.team_id);
  if (league.season_state === 'completed') {
    return { kind: 'over', meta, team: { name: team.name, mine }, rank: null, rankDetail: null, sentence: null,
             line: `Season over — ${ordinal(team.rank)} of ${team.num_teams}` };
  }
  const rank = ordinal(team.rank);
  const rankDetail = [`of ${team.num_teams}`, formatRecord(team.wins, team.losses, team.ties), team.streak].filter(Boolean).join(' · ');
  if (!mine) return { kind: 'pick', meta, team: { name: team.name, mine: false }, rank, rankDetail, sentence: null, line: null };
  const sentence = [lastClause(league.last), magicClause(team, league.playoff), nextClause(league.next)].filter(Boolean).join(' ') || null;
  return { kind: 'normal', meta, team: { name: team.name, mine: true }, rank, rankDetail, sentence, line: null };
}

export function sidebarStatus(league) {
  if (league.error) return null;
  const provider = providerLabel(league.provider);
  if (!isSynced(league)) return `${provider} · not synced`;
  if (!league.team || league.season_state === 'not_started') return `${provider} · not started`;
  return `${provider} · ${ordinal(league.team.rank)} of ${league.team.num_teams}`;
}
